import { message } from 'antd';
import { useCallback } from 'react';
import {
  updateAdminProductAvailability,
  updateAdminProductVariantAvailability,
} from '../../services';
import type { AdminProduct, UseAdminProductCatalogArgs } from '../interfaces';
import { updateVariantAvailabilityInList } from './adminProductForm.utils';
import { getApiErrorMessage } from '../../utils/apiError';

type UseAdminProductAvailabilityActionsArgs = Pick<
  UseAdminProductCatalogArgs,
  'setBaseProducts' | 'setProducts'
>;

const updateMaterialAvailabilityInList = (
  products: AdminProduct[],
  materialPriceId: string,
  isAvailable: boolean
) =>
  products.map((product) => ({
    ...product,
    materialPrices: product.materialPrices.map((material) =>
      material.id === materialPriceId ? { ...material, isAvailable } : material
    ),
  }));

export const useAdminProductAvailabilityActions = ({
  setBaseProducts,
  setProducts,
}: UseAdminProductAvailabilityActionsArgs) => {
  const handleVariantAvailabilityChange = useCallback(async (variantInventoryId: string, isAvailable: boolean) => {
    try {
      await updateAdminProductVariantAvailability(variantInventoryId, isAvailable);
      setBaseProducts((prev) => updateVariantAvailabilityInList(prev, variantInventoryId, isAvailable));
      setProducts((prev) => updateVariantAvailabilityInList(prev, variantInventoryId, isAvailable));
      message.success(isAvailable ? 'Variant enabled.' : 'Variant disabled.');
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to update variant availability.'));
    }
  }, [setBaseProducts, setProducts]);

  const handleAvailabilityChange = useCallback(async (materialPriceId: string, isAvailable: boolean) => {
    try {
      await updateAdminProductAvailability(materialPriceId, isAvailable);
      setBaseProducts((prev) => updateMaterialAvailabilityInList(prev, materialPriceId, isAvailable));
      setProducts((prev) => updateMaterialAvailabilityInList(prev, materialPriceId, isAvailable));
      message.success(isAvailable ? 'Material enabled.' : 'Material disabled.');
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to update availability.'));
    }
  }, [setBaseProducts, setProducts]);

  return { handleAvailabilityChange, handleVariantAvailabilityChange };
};
